import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ExternalLink, FileText, Link2 } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export function QuoteDetailSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=130%',
          pin: true,
          scrub: 0.6,
        },
      });

      // ENTRANCE (0% - 30%)
      scrollTl.fromTo(
        headlineRef.current,
        { y: '-10vh', opacity: 0 },
        { y: 0, opacity: 1, ease: 'none' },
        0
      );

      scrollTl.fromTo(
        cardRef.current,
        { x: '60vw', opacity: 0 },
        { x: 0, opacity: 1, ease: 'none' },
        0.05
      );

      scrollTl.fromTo(
        chipRef.current,
        { y: '20vh', opacity: 0 },
        { y: 0, opacity: 1, ease: 'none' },
        0.15
      );

      // SETTLE (30% - 70%) - hold position with subtle parallax
      const cardContent = cardRef.current?.querySelector('.card-content');
      if (cardContent) {
        scrollTl.fromTo(cardContent, { y: 0 }, { y: -12, ease: 'none' }, 0.3);
      }

      // EXIT (70% - 100%)
      scrollTl.fromTo(
        cardRef.current,
        { scale: 1, x: 0, opacity: 1 },
        { scale: 0.92, x: '-10vw', opacity: 0, ease: 'power2.in' },
        0.7
      );

      scrollTl.fromTo(headlineRef.current, { opacity: 1 }, { opacity: 0, ease: 'power2.in' }, 0.75);

      scrollTl.fromTo(chipRef.current, { opacity: 1 }, { opacity: 0, ease: 'power2.in' }, 0.78);
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-pinned z-40 flex items-center justify-center bg-[#F6F5F2]"
    >
      {/* Headline Group */}
      <div
        ref={headlineRef}
        className="absolute"
        style={{ left: '9vw', top: '10vh', width: '44vw' }}
      >
        <h2
          className="font-display font-bold leading-[1.05] tracking-[-0.02em] text-[#0B0D10]"
          style={{ fontSize: 'clamp(34px, 3.6vw, 52px)' }}
        >
          Every card is a citation.
        </h2>
        <p
          className="mt-4 leading-relaxed text-[#6B6F78]"
          style={{ fontSize: 'clamp(16px, 1.4vw, 20px)', width: '38vw' }}
        >
          Date. Source. Canonical link. No mystery—just the document, as published.
        </p>
      </div>

      {/* Large Receipt Card */}
      <div
        ref={cardRef}
        className="receipt-card absolute p-8"
        style={{ left: '30vw', top: '34vh', width: '52vw', height: '52vh' }}
      >
        <div className="card-content flex h-full flex-col">
          {/* Card Header */}
          <div className="mb-6 flex items-center gap-4 border-b-2 border-[#E1062C] pb-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#E1062C]">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-display text-lg font-bold text-[#0B0D10]">
                Bureau of Labor Statistics
              </p>
              <p className="font-mono text-xs text-[#6B6F78]">
                Characteristics of Minimum Wage Workers, 2023
              </p>
            </div>
            <span className="font-mono text-sm font-medium text-[#E1062C]">March 2024</span>
          </div>

          {/* Quote */}
          <div className="flex-1">
            <p
              className="font-medium leading-relaxed text-[#0B0D10]"
              style={{ fontSize: 'clamp(18px, 1.6vw, 24px)' }}
            >
              "The Bureau of Labor Statistics reports that minimum wage workers tend to be young,
              with 46.6% under age 25. Although workers under age 25 represented only about
              one-fifth of hourly paid workers, they made up nearly half of those who earned the
              federal minimum wage or less."
            </p>
          </div>

          {/* Metadata Row */}
          <div className="mt-6 border-t border-[rgba(11,13,16,0.08)] pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                    Source
                  </p>
                  <p className="text-sm text-[#0B0D10]">BLS</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                    Date
                  </p>
                  <p className="text-sm text-[#0B0D10]">March 2024</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                    Document
                  </p>
                  <p className="text-sm text-[#0B0D10]">CPS Report</p>
                </div>
              </div>

              <a
                href="https://www.bls.gov/opub/reports/minimum-wage/2023/home.htm"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-hover flex items-center gap-2 rounded-full bg-[#E1062C] px-5 py-2.5 font-medium text-white hover:bg-[#c00525]"
              >
                Open source
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Canonical link chip */}
      <div ref={chipRef} className="absolute" style={{ left: '9vw', top: '74vh', width: '18vw' }}>
        <div className="receipt-card-sm flex items-center gap-3 p-4">
          <Link2 className="h-8 w-8 text-[#E1062C]" />
          <div>
            <p className="text-sm font-medium text-[#0B0D10]">Canonical link</p>
            <p className="font-mono text-xs text-[#6B6F78]">Open to verify</p>
          </div>
        </div>
      </div>
    </section>
  );
}
